import { LIST_GC_TIME_MS, LIST_STALE_TIME_MS, useApiClient } from '@klareospace/pharos-lib-front-utils';
import { TEMPLATE_CONFIGURATION_STATUS } from '@klareospace/pharos-lib-types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { createTemplateConfigurationApi, templateConfigurationKeys } from '../api';
import type { SearchTemplateConfigurationsParams, TemplateConfigurationListResult } from '../api';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Statuses included in the connected-shippers list.
 * ARCHIVED is intentionally excluded — soft-deleted configurations must never
 * appear in the management table.
 */
const ACTIVE_STATUSES = [
  TEMPLATE_CONFIGURATION_STATUS.PENDING,
  TEMPLATE_CONFIGURATION_STATUS.ACTIVE,
  TEMPLATE_CONFIGURATION_STATUS.INACTIVE,
] as const;

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseShipperConfigurationsParams extends Omit<SearchTemplateConfigurationsParams, 'statuses'> {
  /** Defaults to `true` — set `false` to skip the fetch entirely (e.g. while a permission is missing). */
  enabled?: boolean;
  /** Overrides the default connected-shippers status set — e.g. to request ACTIVE-only from the server. Defaults to the Settings page's `PENDING`/`ACTIVE`/`INACTIVE` list. */
  statuses?: SearchTemplateConfigurationsParams['statuses'];
}

export function useShipperConfigurations({
  enabled = true,
  statuses = [...ACTIVE_STATUSES],
  ...params
}: UseShipperConfigurationsParams): UseQueryResult<TemplateConfigurationListResult> {
  const client = useApiClient('deliveries');
  const api = createTemplateConfigurationApi(client);

  const searchParams: SearchTemplateConfigurationsParams = { ...params, statuses };

  return useQuery({
    enabled,
    gcTime: LIST_GC_TIME_MS,
    queryFn: () => api.searchTemplateConfigurations(searchParams),
    queryKey: templateConfigurationKeys.search(searchParams),
    staleTime: LIST_STALE_TIME_MS,
  });
}
